import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, DownloadSimple, Envelope } from "@phosphor-icons/react";
import { toast } from "sonner";

export function SubscribersTab() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [q, setQ]               = useState("");
  const [loading, setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);

  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/newsletter/subscribers", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  }, [page, q]);

  useEffect(() => { load(); }, [load]);

  // Debounce search
  useEffect(() => {
    setPage(1);
  }, [q]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      const resp = await api.get(
        `/newsletter/subscribers/export?${params.toString()}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a   = document.createElement("a");
      a.href    = url;
      a.download = "subscribers.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="subscribers-tab">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div>
          <p className="text-sm text-zinc-500">
            {total > 0
              ? `${total.toLocaleString()} subscriber${total !== 1 ? "s" : ""}`
              : "No subscribers yet"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlass
              size={13}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
            />
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Filter by email…"
              className="!pl-9 h-8 text-xs w-56"
              data-testid="subscribers-search"
            />
          </div>
          {/* Export */}
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1.5"
            onClick={exportCsv}
            disabled={exporting || total === 0}
            data-testid="export-subscribers-btn"
          >
            <DownloadSimple size={13} />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Email</th>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Subscribed</th>
              <th className="px-5 py-3 font-semibold">Source</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-5 py-12 text-center text-zinc-400">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-16 text-center">
                  <div className="flex flex-col items-center gap-2 text-zinc-400">
                    <Envelope size={28} weight="light" />
                    <p className="text-sm">
                      {q ? "No subscribers match your search" : "No newsletter subscribers yet"}
                    </p>
                    {!q && (
                      <p className="text-xs text-zinc-400 max-w-xs">
                        Subscribers will appear here once someone fills in the
                        'Join our newsletter' form on your public website.
                      </p>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              items.map(s => (
                <tr
                  key={s.id}
                  className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50"
                  data-testid={`subscriber-row-${s.id}`}
                >
                  <td className="px-5 py-3 font-medium text-zinc-900">{s.email}</td>
                  <td className="px-5 py-3 text-zinc-600">{s.name || <span className="text-zinc-300">—</span>}</td>
                  <td className="px-5 py-3 text-zinc-500 text-xs whitespace-nowrap">
                    {s.subscribed_at
                      ? new Date(s.subscribed_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-[11px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                      {s.source || "website"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-zinc-600">
          <span>
            Page {page} of {totalPages} ({total.toLocaleString()} total)
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              data-testid="subscribers-prev"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              data-testid="subscribers-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
